import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { USER_ROLES, getRoleDisplayName } from '../../config/userRoles';
import { ThemeToggle } from '../../context/ThemeContext';
import { SetPasswordModal } from '../../components/auth/SetPasswordModal';

/**
 * CompleteLogin - Handles the magic link callback
 * Redirects user based on their role after successful login
 * Shows SetPasswordModal if user doesn't have a password yet
 */
export default function CompleteLogin() {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  
  const { completeMagicLinkSignIn, userProfile, error, hasPassword } = useUnifiedAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const completeLogin = async () => {
      try {
        const user = await completeMagicLinkSignIn();
        
        if (user) {
          setStatus('success');
          // Check if this is the first login (user has no password set)
          // Will be checked in the next useEffect when userProfile is loaded
        } else {
          // Not a valid sign-in link
          setStatus('error');
          setErrorMessage('Dies ist kein gültiger Login-Link.');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.message || 'Ein Fehler ist aufgetreten.');
      }
    };

    completeLogin();
  }, []);

  // Check for first login and show password modal
  useEffect(() => {
    if (status === 'success' && userProfile) {
      // Check if user has a password
      const userHasPassword = hasPassword();
      
      if (!userHasPassword) {
        // First login - show password modal
        setIsFirstLogin(true);
        setShowSetPasswordModal(true);
      } else {
        // User has password, redirect immediately
        redirectToDashboard();
      }
    }
  }, [status, userProfile]);

  const redirectToDashboard = () => {
    const redirectPath = getRedirectPath(userProfile.role);
    
    // Short delay to show success message
    setTimeout(() => {
      navigate(redirectPath, { replace: true });
    }, 1500);
  };

  const handlePasswordSet = () => {
    setShowSetPasswordModal(false);
    // After setting password, redirect to dashboard
    redirectToDashboard();
  };

  const getRedirectPath = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return '/admin/dashboard';
      case USER_ROLES.TEACHER:
        return '/dashboard';
      case USER_ROLES.STUDENT:
      default:
        return '/student/dashboard';
    }
  };

  // Processing state
  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg className="animate-spin w-16 h-16 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Anmeldung wird verarbeitet...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Bitte warte einen Moment.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Erfolgreich angemeldet!
          </h2>
          {userProfile && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Willkommen, <strong className="text-gray-900 dark:text-white">{userProfile.displayName}</strong>!
              <br />
              <span className="text-sm text-gray-500 dark:text-gray-500">
                ({getRoleDisplayName(userProfile.role)})
              </span>
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Du wirst gleich weitergeleitet...
          </p>
        </div>
        
        {/* Set Password Modal for First Login */}
        <SetPasswordModal 
          isOpen={showSetPasswordModal}
          onClose={handlePasswordSet}
          required={isFirstLogin}
        />
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Anmeldung fehlgeschlagen
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {errorMessage || error || 'Ein unbekannter Fehler ist aufgetreten.'}
        </p>
        
        <div className="space-y-3">
          <button
            onClick={() => navigate('/auth/login')}
            className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg theme-transition"
          >
            Erneut versuchen
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg theme-transition"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    </div>
  );
}
